import { useRef, useEffect } from "react";
import { Chart } from "chart.js/auto";
import {
  BoxPlotController,
  BoxAndWiskers,
} from "@sgratzl/chartjs-chart-boxplot";

Chart.register(BoxPlotController, BoxAndWiskers);
// TODO: ADDRESS BUG IN DOUBLE WHISKER DRIVETIME
const BoxPlotChart = ({ dataValues, clickRedirect = true, ylabel }) => {
  const chartRef = useRef(null);
  useEffect(() => {
    const ctx = chartRef.current.getContext("2d");

    // Outlier Detection
    const identifyOutliers = (point, data) => {
      const lowerFence = data.whiskerMin;
      const upperFence = data.whiskerMax;

      if (point.y < lowerFence) return "lower"; // Lower bound outlier
      if (point.y > upperFence) return "upper"; // Upper bound outlier
      return null; // Not an outlier
    };

    const quartileData = dataValues?.quartiles?.[0];
    // Separate data into outliers and normal points
    // todo: stopped here
    const scatterData = dataValues?.values.map((point) => {
      const outlierType = identifyOutliers(point, quartileData);
      return {
        ...point,
        outlierType, // "upper", "lower", or null
      };
    });

    // Split outliers into upper and lower
    const upperOutliers = scatterData.filter(
      (point) => point.outlierType === "upper"
    );
    const maxValueX = quartileData?.max;

    const lowerOutliers = scatterData.filter(
      (point) => point.outlierType === "lower"
    );
    const minValueX = quartileData?.min;

    const normalPoints = scatterData.filter((point) => !point.outlierType);

    // Determine colors based on prop
    const lowerBoundColor =
      dataValues?.outlier === "good"
        ? "#bd0606d0"
        : dataValues?.outlier === "bad"
        ? "#00a63f"
        : "#8fcbf2be";
    const upperBoundColor =
      dataValues?.outlier === "good"
        ? "#00a63f"
        : dataValues?.outlier === "bad"
        ? "#bd0606d0"
        : "#8fcbf2be";

    const data = {
      labels: dataValues?.labels,
      datasets: [
        {
          label: "Boxplots",
          type: "boxplot",
          data: dataValues?.quartiles,
          backgroundColor: "#8fcbf2be",
          borderColor: "#0096FA",
          borderWidth: 1,
        },
        {
          label: "Datapoints",
          type: "scatter",
          data: normalPoints, // Non-outliers
          backgroundColor: "#0075d2",
          pointRadius: 3,
        },
        {
          label: "Upper Bound Outliers",
          type: "scatter",
          data: upperOutliers, // Only upper bound outliers
          backgroundColor: upperBoundColor,
          pointRadius: 3,
        },
        {
          label: "Lower Bound Outliers",
          type: "scatter",
          data: lowerOutliers, // Only lower bound outliers
          backgroundColor: lowerBoundColor,
          pointRadius: 3,
        },
      ],
    };

    const config = {
      type: "boxplot",
      data: data,
      options: {
        responsive: true,
        scales: {
          x: {
            type: "category",
            offset: true,
            grid: {
              offset: true,
            },
            ticks: {
              font: {
                size: 10, // Adjust the font size for the x-axis labels here
              },
            },
          },
          y: {
            beginAtZero: false,
            suggestedMax: maxValueX > 1.5 ? maxValueX + 1 : maxValueX + 0.05,
            suggestedMin: minValueX >= 1 ? minValueX - 2 : 0,
            ticks: {
              stepSize:
                maxValueX > 1.5
                  ? Math.pow(
                      10,
                      Math.floor(Math.log10(maxValueX - minValueX / 5))
                    )
                  : 0.05,
            },
            title: {
              display: true, // Enable the y-axis label
              text: ylabel, // Set the label text
              font: {
                size: 11, // Font size for the label
                family: "sans-serif", // Font family for the label
              },
              color: "#333", // Font color for the label
            },
          },
        },
        plugins: {
          labels: {
            font: {
              size: 5, // Adjust the font size here
            },
          },
          legend: {
            position: "top",
            onClick: (e, legendItem, legend) => {
              const chart = legend.chart;
              const datasetIndex = legendItem.datasetIndex;
              const dataset = chart.data.datasets[datasetIndex];

              // Toggle visibility for the selected dataset
              dataset.hidden = !dataset.hidden;
              chart.update();
            },
          },
          tooltip: {
            callbacks: {
              title: (context) => context[0].label,
              // Customize the tooltip for the boxplot
              label: function (context) {
                // TODO: REMOVE HARDCODING OF VEHICLE ID BELOW
                const vehicle_id = "VH001";

                const dataset = context.dataset;
                const dataPoint = dataset.data[context.dataIndex];

                if (dataset.type === "boxplot") {
                  return [
                    // `Max: ${dataPoint.max}`,
                    `Q3: ${dataPoint.q3}`,
                    `Median: ${dataPoint.median}`,
                    `Q1: ${dataPoint.q1}`,
                    // `Min: ${dataPoint.min}`,
                  ];
                }

                // Customize the tooltip for scatter points
                if (dataset.type === "scatter") {
                  const point = dataPoint; // Scatter point object
                  if (point.outlierType === "upper") {
                    return [
                      `Vehicle ${vehicle_id} (${point.x})`,
                      `${point.y} (Upper Outlier)`,
                    ];
                  } else if (point.outlierType === "lower") {
                    return [
                      `Vehicle ${vehicle_id} (${point.x})`,
                      `${point.y} (Lower Outlier)`,
                    ];
                  } else {
                    return [`Vehicle ${vehicle_id}`, `${point.y}`];
                  }
                }
                return context.raw; // Default fallback
              },
            },
          },
        },
        // onClick: (event, elements) => {
        //   if (elements.length > 0 && clickRedirect) {
        //     // TODO: REMOVE HARDCODING OF VEHICLE ID BELOW
        //     // const elementIndex = elements[0].index;
        //     // const datasetIndex = elements[0].datasetIndex;
        //     // const point = chart.data.datasets[datasetIndex].data[elementIndex];
        //     // const vehicle_id = point?.x;
        //     const vehicle_id = "VH001";
        //     if (vehicle_id) {
        //       // Injecting the attribute to the URL and redirecting
        //       const url = `advanced-analytics?activeTab=2&outlier-profile=${vehicle_id}`;
        //       window.location.href = url;
        //     }
        //   }
        // },
      },
    };

    const chart = new Chart(ctx, config);

    return () => {
      chart.destroy();
    };
  }, [dataValues]);

  return <canvas ref={chartRef} />;
};

export default BoxPlotChart;
